export const autoRecordFeature = "NEPH_FF_RECORDING_START_BY_DEFAULT";
export const oidcFeature = "NEPH_FF_OIDC";
export const resetEncounters = "NEPH_FF_DEMO_RESET";
export const careGapDetectionFlag = "NEPH_FF_CARE_GAP";
export const gCodesFeature = "NEPH_FF_G_CODES";
export const g2211SuppressFeature = "NEPH_FF_G2211_SUPPRESS";
export const billingOldViewFeature = "NEPH_FF_BILLING_OLD_VIEW";
export const dataReadyPifSuppressFeature = "NEPH_FF_DATA_READY_PIF_SUPPRESS";
export const restrictToChromeFlag = "NEPH_FF_RESTRICT_TO_CHROME";
export const keyClinicalFactsFeature = "NEPH_FF_KEY_CLINICAL_FACTS";
export const onboardingFeature = "NEPH_FF_ONBOARDING";
export const roomNumberPushNotifications = "NEPH_FF_ROOM_NUMBER_PUSH_NOTIFICATIONS";
export const microphoneVisualizerFeature = "NEPH_FF_MICROPHONE_VISUALIZER";
export const sentryMonitoringFeature = "NEPH_FF_SENTRY_MONITORING";
export const sentryDsn = "NEPH_FF_SENTRY_DSN";
export const sentryOrg = "NEPH_FF_SENTRY_ORG";
export const sentryProject = "NEPH_FF_SENTRY_PROJECT";
export const sentryAuthToken = "NEPH_FF_SENTRY_AUTH_TOKEN";
export const sentryEnvironment = "NEPH_FF_SENTRY_ENVIRONMENT";
export const sentryReplaySessionSampleRate = "NEPH_FF_SENTRY_REPLAY_SESSION_SAMPLE_RATE";
export const sentryReplayErrorSampleRate = "NEPH_FF_SENTRY_REPLAY_ERROR_SAMPLE_RATE";
export const annotationInterfaceFeature = "NEPH_FF_ANNOTATION_INTERFACE";
export const recentActivityFeature = "NEPH_FF_RECENT_ACTIVITY";
export const speechToTextFeature = "NEPH_FF_SPEECH_TO_TEXT";
export const audioUploadFeature = "NEPH_FF_AUDIO_UPLOAD";
export const kfreEnabledAccountsFeature = "NEPH_FF_KFRE_ENABLED_ACCOUNTS";
export const restrictedOrderAccountsFeature = "NEPH_FF_RESTRICTED_ORDER_ACCOUNTS";
export const mcpTokenFeature = "NEPH_FF_MCP_TOKEN";
export const smartRecommendationsBetaBadgeFeature = "NEPH_FF_SMART_RECOMMENDATIONS_BETA_BADGE";
export const labNormalizationIndicatorFeature = "NEPH_FF_LAB_NORMALIZATION_INDICATOR";
export const labSanityCheckUiFeature = "NEPH_FF_LAB_SANITY_CHECK_UI";
export const priorDiagnosisSuggestionsFeature = "NEPH_FF_HPI_ENABLED_ACCOUNT_IDS";
export const diagnosisImpressionSuppressFeature = "NEPH_FF_DIAGNOSIS_IMPRESSION_SUPPRESS";
// Per-diagnosis clinical Status is hidden by default (flag absent or off): no Status field on the
// interim screen and no "Status:" line in the endnote, so it is not written back to the EMR either.
// The value is still generated and kept in smart_note_data. Flag on restores the Status display.
export const diagnosisStatusDisplayFeature = "NEPH_FF_DIAGNOSIS_STATUS_DISPLAY";
export const bmiVitalsSuppressFeature = "NEPH_FF_BMI_VITALS_SUPPRESS";
export const reasonForVisitSuppressFeature = "NEPH_FF_REASON_FOR_VISIT_SUPPRESS";
export const recommendationSuppressedTabSuppressFeature = "NEPH_FF_RECC_SUPPRESS";
// Lab and imaging orders in the endnote's per-diagnosis Plan read "Order <name>" by default (NKF-1493).
// Flag on turns the prefix off: for the accounts in attributes.account_ids, or for everyone without it.
export const planOrderPrefixSuppressFeature = "NEPH_FF_PLAN_ORDER_PREFIX_SUPPRESS";
// Kill switch for the fix that stops a slow refresh being reported as a failed one.
// Flag off (the default) keeps the fix active; flag on restores the previous behaviour where
// exceeding the polling ceiling produced a "timeout" failure with a Retry CTA.
export const falseRefreshFailuresSuppressFeature = "NEPH_FF_FALSE_REFRESH_FAILURES_SUPPRESS";

export interface FeatureFlagData {
  enabled: boolean;
  attributes?: Record<string, any>;
}

export type FeatureFlags = Record<string, boolean | FeatureFlagData | undefined>;

export function isFeatureEnabled(data: FeatureFlags | null | undefined, flag: string): boolean {
  // Support both boolean format (legacy) and object format with {enabled: boolean, attributes: {...}}
  const flagData = data?.[flag];

  if (flagData === undefined || flagData === null) {
    return false;
  }

  // If it's a boolean (legacy format), return it directly
  if (typeof flagData === "boolean") {
    return flagData;
  }

  // If it's an object with 'enabled' property
  if (typeof flagData === "object" && "enabled" in flagData) {
    return flagData.enabled === true;
  }

  // Default to false
  return false;
}

export function getFeatureAttributes(data: FeatureFlags | null | undefined, flag: string): Record<string, any> | null {
  // Return attributes for a feature flag if they exist
  const flagData = data?.[flag];

  if (!flagData || typeof flagData !== "object") {
    return null;
  }

  return (flagData as FeatureFlagData).attributes || null;
}

export function isFeatureEnabledForAccount(
  data: FeatureFlags | null | undefined,
  flag: string,
  accountId: number | null | undefined
): boolean {
  // First check if the feature is enabled at all
  if (!isFeatureEnabled(data, flag)) {
    return false;
  }

  // Get the attributes
  const attributes = getFeatureAttributes(data, flag);

  // If there are no attributes or no account_ids, feature is enabled for all
  if (!attributes || !attributes.account_ids) {
    return true;
  }

  // Check if account_id is not provided
  if (!accountId) {
    return false;
  }

  // Check if the current account_id is in the allowed list
  const allowedAccountIds = attributes.account_ids;
  if (Array.isArray(allowedAccountIds)) {
    return allowedAccountIds.includes(accountId);
  }

  return false;
}

export function isFeatureEnabledForUser(data: FeatureFlags | null | undefined, flag: string, userId: number | null | undefined): boolean {
  // First check if the feature is enabled at all
  if (!isFeatureEnabled(data, flag)) {
    return false;
  }

  // Get the attributes
  const attributes = getFeatureAttributes(data, flag);

  // If there are no attributes or no user_ids, consider feature disabled
  if (!attributes || !attributes.user_ids) {
    return false;
  }

  // Check if userId is not provided
  if (!userId) {
    return false;
  }

  // Check if the current userId is in the allowed list
  const allowedUserIds = attributes.user_ids;
  if (Array.isArray(allowedUserIds)) {
    return allowedUserIds.includes(userId);
  }

  return false;
}
